import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth as useClerkAuth, useClerk } from "@clerk/react";
import api, { setClerkTokenGetter, withTimeout } from "@/lib/api";

const AuthContext = createContext(null);

const AUTH_PATHS = new Set(["/login", "/sign-up", "/waitlist", "/"]);
const TOKEN_MS = 4000;
const ME_MS = 10000;
const AUTH_ATTEMPTS = 2;
const LOADING_CAP_MS = 12000;
const CLERK_LOAD_CAP_MS = 15000;
const FLICKER_MS = 800;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const { getToken, isLoaded, isSignedIn } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const userRef = useRef(user);
  userRef.current = user;
  const isLoadedRef = useRef(isLoaded);
  isLoadedRef.current = isLoaded;
  const authGen = useRef(0);

  const readToken = useCallback(async ({ skipCache } = {}) => {
    try {
      return await withTimeout(
        getToken({ leewayInSeconds: 60, skipCache: Boolean(skipCache) }),
        TOKEN_MS,
        "clerk_token_timeout",
      );
    } catch {
      return null;
    }
  }, [getToken]);

  const checkAuth = useCallback(async ({ silent = false } = {}) => {
    const my = ++authGen.current;
    const still = () => my === authGen.current;
    const finish = (nextUser, error) => {
      if (!still()) return;
      if (nextUser !== undefined) setUser(nextUser);
      setSessionError(error || null);
      setLoading(false);
    };

    try {
      let signedIn = Boolean(isSignedIn);
      if (!signedIn) {
        const hadUser = userRef.current && userRef.current !== false;
        if (hadUser) {
          await new Promise((r) => setTimeout(r, FLICKER_MS));
          if (!still()) return;
          const recovered = await readToken({ skipCache: true });
          if (recovered) {
            signedIn = true;
          } else {
            finish(false, null);
            return;
          }
        } else {
          finish(false, null);
          return;
        }
      }

      const hadUser = userRef.current && userRef.current !== false;
      if (!silent && !hadUser && still()) setLoading(true);
      if (still()) setSessionError(null);

      const token = await readToken();
      if (!still()) return;
      if (!token) {
        finish(
          hadUser ? undefined : false,
          "Clerk signed you in but no session token was ready. Retry, or sign out.",
        );
        return;
      }

      let lastErr = null;
      for (let attempt = 0; attempt < AUTH_ATTEMPTS && still(); attempt += 1) {
        try {
          const { data } = await api.get("/auth/me", {
            skipAuthRedirect: true,
            timeout: ME_MS,
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!still()) return;
          setUser(data);
          setSessionError(null);
          setLoading(false);
          return;
        } catch (err) {
          lastErr = err;
          await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
        }
      }
      if (!still()) return;
      console.error("Clerk session present but /auth/me failed", lastErr);
      finish(
        hadUser ? undefined : false,
        "We signed you in with Clerk but could not open your MarkovLab account. Retry, or sign out.",
      );
    } catch (err) {
      console.error("Auth handshake failed", err);
      const hadUser = userRef.current && userRef.current !== false;
      finish(
        hadUser ? undefined : false,
        "Sign-in did not finish. Retry, or sign out and try again.",
      );
    }
  }, [isSignedIn, readToken]);

  useEffect(() => {
    setClerkTokenGetter(async ({ skipCache } = {}) => {
      if (!isSignedIn) return null;
      return readToken({ skipCache });
    });
  }, [readToken, isSignedIn]);

  useEffect(() => {
    if (!isLoaded) return;
    checkAuth({ silent: Boolean(userRef.current && userRef.current !== false) });
  }, [checkAuth, isLoaded, isSignedIn]);

  useEffect(() => {
    if (!loading) return undefined;
    const id = setTimeout(() => {
      if (userRef.current && userRef.current !== false) {
        setLoading(false);
        return;
      }
      setSessionError((e) => e || "Sign-in is taking too long. Retry, or sign out and try again.");
      setUser((u) => (u && u !== false ? u : false));
      setLoading(false);
    }, LOADING_CAP_MS);
    return () => clearTimeout(id);
  }, [loading]);

  useEffect(() => {
    if (isLoaded) return undefined;
    const id = setTimeout(() => {
      if (isLoadedRef.current) return;
      setSessionError("Clerk did not finish loading. Check that accounts.markovlab.app is reachable, then retry.");
      setUser(false);
      setLoading(false);
    }, CLERK_LOAD_CAP_MS);
    return () => clearTimeout(id);
  }, [isLoaded]);

  // Keep the Clerk JWT warm so idle tabs don't drop the session.
  useEffect(() => {
    if (!isSignedIn) return undefined;
    const tick = () => { readToken({ skipCache: true }); };
    const id = setInterval(tick, 45000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isSignedIn, readToken]);

  useEffect(() => {
    if (loading || !user) return;
    const path = location.pathname;
    if (AUTH_PATHS.has(path) || path.startsWith("/login") || path.startsWith("/sign-up")) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    try {
      if (isSignedIn) await clerkSignOut();
    } catch (_e) { /* ignore */ }
    setUser(false);
    setSessionError(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, checkAuth, clerkSignedIn: isLoaded && isSignedIn, sessionError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
